import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDoc, doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Container, Form, Button, Alert, Image, Row, Col, InputGroup } from 'react-bootstrap';
import { goods_categories, categoryStructure, conditionOptions, age_categories } from '../menu_options/dropdown_options';
import { cityByProvince } from '../menu_options/location_options';
import { FaArrowLeft } from 'react-icons/fa';
import './editad.css';

const EditAd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [ad, setAd] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    ItemName: '',
    Price: '',
    description: '',
    phonenumber: '',
    province: '',
    city: '',
    condition: '',
    ageRange: '',
    YearModel: '',
    brand: ''
  });
  const [newImages, setNewImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [cities, setCities] = useState([]);

  const provinces = [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
    'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
  ];
  
  // Add year options
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const adDoc = await getDoc(doc(db, 'ads', id));
        if (adDoc.exists()) {
          const adData = adDoc.data();
          setAd(adData);
          setFormData({
            category: adData.category || '',
            subcategory: adData.subcategory || '',
            ItemName: adData.ItemName || '',
            Price: adData.Price || '',
            description: adData.description || '',
            phonenumber: adData.phonenumber || '',
            province: adData.province || '',
            city: adData.city || '',
            condition: adData.condition || '',
            ageRange: adData.ageRange || '',
            YearModel: adData.YearModel || '',
            brand: adData.brand || ''
          });
          
          // Set subcategories based on the ad's category
          if (adData.category) {
            setSubcategories(categoryStructure[adData.category] || []);
          }
          
          // Set cities based on the ad's province
          if (adData.province) {
            setCities(cityByProvince[adData.province] || []);
          }
        } else {
          setError('Ad not found');
        }
      } catch (err) {
        setError('Error fetching ad');
        console.error(err);
      }
      setLoading(false);
    };

    fetchAd();
  }, [id]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.category) {
      setSubcategories(categoryStructure[formData.category] || []);
      
      // Reset subcategory when category changes if the current subcategory
      // is not in the new category's subcategories
      const currentSubcategory = formData.subcategory;
      const newSubcategories = categoryStructure[formData.category] || [];
      if (currentSubcategory && !newSubcategories.includes(currentSubcategory)) {
        setFormData(prev => ({...prev, subcategory: ''}));
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.category]);

  // Add useEffect for updating cities when province changes
  useEffect(() => {
    if (formData.province) {
      setCities(cityByProvince[formData.province] || []);
    } else {
      setCities([]);
    }
  }, [formData.province]);

  const formatNumber = (value) => {
    const number = value.toString().replace(/\D/g, '');
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'Price') {
      const numericValue = value.replace(/\D/g, '');
      setFormData(prevState => ({
        ...prevState,
        [name]: numericValue,
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files);
    
    // Check for maximum 5 images total (existing + new)
    const totalImages = (ad.imageUrls?.length || 0) + newImages.length + files.length;
    if (totalImages > 5) {
      setError(`Maximum 5 images allowed per ad. You can add ${5 - (ad.imageUrls?.length || 0) - newImages.length} more.`);
      return;
    }
    
    // Check file sizes
    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError(`Some files exceed the maximum size of 10MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    setNewImages(prevImages => [...prevImages, ...files]);
  };

  const handleImageDelete = async (index, isNewImage) => {
    if (isNewImage) {
      setNewImages(prevImages => prevImages.filter((_, i) => i !== index));
    } else {
      const imageUrl = ad.imageUrls[index];
      const storage = getStorage();
      const imageRef = ref(storage, imageUrl);
      try {
        await deleteObject(imageRef);
        const updatedImageUrls = ad.imageUrls.filter((_, i) => i !== index);
        await updateDoc(doc(db, 'ads', id), { imageUrls: updatedImageUrls });
        setAd(prevAd => ({ ...prevAd, imageUrls: updatedImageUrls }));
      } catch (err) {
        console.error('Error deleting image:', err);
        setError('Failed to delete image');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const storage = getStorage();
      const newImageUrls = await Promise.all(
        newImages.map(async (image) => {
          const imageRef = ref(storage, `ads/${id}/${Date.now()}_${image.name}`);
          await uploadBytes(imageRef, image);
          return getDownloadURL(imageRef);
        })
      );

      const updatedImageUrls = [...(ad.imageUrls || []), ...newImageUrls];

      await updateDoc(doc(db, 'ads', id), {
        ...formData,
        Price: Number(formData.Price),
        YearModel: formData.YearModel ? Number(formData.YearModel) : null,
        imageUrls: updatedImageUrls,
      });

      navigate('/my-listings');
    } catch (err) {
      setError('Error updating ad');
      console.error(err);
    }

    setLoading(false);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!ad) return <Alert variant="warning">Ad not found</Alert>;

  return (
    <Container className="mt-5">
      <div className="d-flex align-items-center mb-4">
        <Button 
          variant="link" 
          className="back-button p-0 me-3" 
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </Button>
        <h2 className="mb-0">Edit Ad</h2>
      </div>
      <Row className="justify-content-center">
        <Col md={8} lg={6} className="edit-ad-container">
          <Form onSubmit={handleSubmit} className="edit-ad-form">
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select name="category" value={formData.category} onChange={handleChange} required>
                <option value="">Select Category</option>
                {goods_categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Form.Select>
            </Form.Group>
            
            {formData.category && (
              <div className="subcategory-container">
                <Form.Group>
                  <Form.Label>Subcategory</Form.Label>
                  <Form.Select name="subcategory" value={formData.subcategory} onChange={handleChange} required>
                    <option value="">Select Subcategory</option>
                    {subcategories.map((subcategory) => (
                      <option key={subcategory} value={subcategory}>{subcategory}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Brand</Form.Label>
              <Form.Control 
                type="text" 
                name="brand" 
                value={formData.brand} 
                onChange={handleChange} 
                placeholder="Enter brand name"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Item Name</Form.Label>
              <Form.Control
                type="text"
                name="ItemName"
                value={formData.ItemName}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Age Range</Form.Label>
              <Form.Select name="ageRange" value={formData.ageRange} onChange={handleChange} required>
                <option value="">Select Age Range</option>
                {age_categories.map((age) => (
                  <option key={age} value={age}>{age}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Price</Form.Label>
              <InputGroup>
                <InputGroup.Text>R</InputGroup.Text>
                <Form.Control
                  type="text"
                  name="Price"
                  value={formatNumber(formData.Price)}
                  onChange={handleChange}
                  required
                  className="price-input"
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Year Model</Form.Label>
              <Form.Select 
                name="YearModel" 
                value={formData.YearModel} 
                onChange={handleChange}
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Add Images</Form.Label>
              <Form.Control
                type="file"
                onChange={handleImageAdd}
                accept="image/*"
                multiple
              />
              <small className="text-muted">
                Maximum 5 images total allowed, each image must be less than 10MB.
                Current count: {(ad.imageUrls?.length || 0) + newImages.length}/5
              </small>
            </Form.Group>
            <Row className="image-preview-row">
              {ad.imageUrls && ad.imageUrls.map((url, index) => (
                <Col key={index} xs={6} md={4} lg={3} className="mb-3">
                  <div className="image-preview-container">
                    <Image src={url} thumbnail className="image-preview" />
                    <Button
                      variant="danger"
                      size="sm"
                      className="delete-image-btn"
                      onClick={() => handleImageDelete(index, false)}
                    >
                      X
                    </Button>
                  </div>
                </Col>
              ))}
              {newImages.map((image, index) => (
                <Col key={`new-${index}`} xs={6} md={4} lg={3} className="mb-3">
                  <div className="image-preview-container">
                    <Image src={URL.createObjectURL(image)} thumbnail className="image-preview" />
                    <Button
                      variant="danger"
                      size="sm"
                      className="delete-image-btn"
                      onClick={() => handleImageDelete(index, true)}
                    >
                      X
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Condition</Form.Label>
              <Form.Select name="condition" value={formData.condition} onChange={handleChange} required>
                <option value="">Select Condition</option>
                {conditionOptions.map((condition) => (
                  <option key={condition} value={condition}>{condition}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className='personal-section'>
              <h5>Personal Information</h5>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                name="phonenumber"
                value={formData.phonenumber}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Province</Form.Label>
              <Form.Select name="province" value={formData.province} onChange={handleChange} required>
                <option value="">Select Province</option>
                {provinces.map((province) => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </Form.Select>
            </Form.Group>
            {formData.province && (
              <Form.Group className="mb-3">
                <Form.Label>City</Form.Label>
                <Form.Select name="city" value={formData.city} onChange={handleChange} required>
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Button variant="primary" type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Updating...' : 'Update Ad'}
            </Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default EditAd;